"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get ClinicIntegrationEmrInstanceSchema () {
        return ClinicIntegrationEmrInstanceSchema;
    },
    get ClinicIntegrationStatusResponseSchema () {
        return ClinicIntegrationStatusResponseSchema;
    },
    get INGEST_LAG_ALARM_SECONDS () {
        return INGEST_LAG_ALARM_SECONDS;
    },
    get IngestLagSchema () {
        return IngestLagSchema;
    },
    get IngestPollSchema () {
        return IngestPollSchema;
    },
    get IntegrationEmrInstanceSchema () {
        return IntegrationEmrInstanceSchema;
    },
    get IntegrationLinkBackfillSchema () {
        return IntegrationLinkBackfillSchema;
    },
    get IntegrationLinkSchema () {
        return IntegrationLinkSchema;
    },
    get IntegrationOverviewClinicSchema () {
        return IntegrationOverviewClinicSchema;
    },
    get IntegrationOverviewResponseSchema () {
        return IntegrationOverviewResponseSchema;
    },
    get MISSING_FILES_WINDOW_DAYS () {
        return MISSING_FILES_WINDOW_DAYS;
    },
    get RESULT_FILE_MISSING_REASONS () {
        return RESULT_FILE_MISSING_REASONS;
    },
    get RESULT_FILE_STATUSES () {
        return RESULT_FILE_STATUSES;
    },
    get ResultCountsSchema () {
        return ResultCountsSchema;
    }
});
const _zod = require("zod");
const _idis2golinks = require("./idis2go-links");
const _tenantlifecycle = require("./tenant-lifecycle");
const INGEST_LAG_ALARM_SECONDS = 900;
const MISSING_FILES_WINDOW_DAYS = 7;
const RESULT_FILE_STATUSES = [
    'waiting',
    'copied',
    'missing'
];
const RESULT_FILE_MISSING_REASONS = [
    'not_found',
    'no_media_row',
    'too_large',
    'bad_media_ref',
    'bad_media_path'
];
const COUNT = _zod.z.number().int().nonnegative();
const SECONDS = _zod.z.number().int().nonnegative();
const IngestLagSchema = _zod.z.object({
    /** The last time a poll read up to IDIS2GO's newest row. Null before the first run. */ lastCaughtUpAt: _zod.z.iso.datetime().nullable(),
    /** Seconds since then. Null before the first run. */ lagSeconds: SECONDS.nullable(),
    /** True when the lag is over `INGEST_LAG_ALARM_SECONDS`, or ingest never ran. */ stale: _zod.z.boolean()
}).strict();
const IngestPollSchema = _zod.z.object({
    /** False before ingest's first run, when there is no poll position yet. */ started: _zod.z.boolean(),
    /**
     * The last time an ingest run wrote its state: took its lease, polled,
     * or moved the poll position. Null before the first run.
     */ lastRunAt: _zod.z.iso.datetime().nullable(),
    lastRunAgeSeconds: SECONDS.nullable()
}).strict();
const ResultCountsSchema = _zod.z.object({
    pending: COUNT,
    available: COUNT,
    delivered: COUNT,
    failed: COUNT
}).strict();
const IntegrationEmrInstanceSchema = _zod.z.object({
    /** The id the EMR signs with. */ instanceId: _zod.z.string(),
    name: _zod.z.string(),
    status: _zod.z.enum([
        'active',
        'disabled'
    ]),
    /** The EMR's last verified request. Null until it first checks in. */ lastSeenAt: _zod.z.iso.datetime().nullable()
}).strict();
const IntegrationOverviewClinicSchema = _zod.z.object({
    tenantId: _zod.z.uuid(),
    name: _zod.z.string(),
    slug: _zod.z.string(),
    status: _zod.z.enum(_tenantlifecycle.TENANT_STATUSES),
    files: _zod.z.object({
        waiting: COUNT,
        missing: COUNT
    }).strict(),
    /** When the oldest file still waiting was first seen. Null when none waits. */ oldestWaitingFileAt: _zod.z.iso.datetime().nullable(),
    oldestWaitingFileAgeSeconds: SECONDS.nullable(),
    results: ResultCountsSchema,
    /** Oldest first. */ emrInstances: _zod.z.array(IntegrationEmrInstanceSchema),
    enabledIdis2goLinks: COUNT
}).strict();
const IntegrationOverviewResponseSchema = _zod.z.object({
    /** The database time the answer was read at. Ages are measured from it. */ checkedAt: _zod.z.iso.datetime(),
    ingest: IngestLagSchema,
    poll: IngestPollSchema,
    /**
     * IDIS2GO customers no clinic holds, and the visits ingest skipped for
     * them. `skippedVisits` is a lifetime count.
     */ unlinkedCustomers: _zod.z.object({
        count: COUNT,
        skippedVisits: COUNT
    }).strict(),
    /** Every clinic tenant, by name. The platform tenant is left out. */ clinics: _zod.z.array(IntegrationOverviewClinicSchema)
}).strict();
const ClinicIntegrationEmrInstanceSchema = IntegrationEmrInstanceSchema.extend({
    /**
   * Results the EMR can fetch (available or delivered) that EMRA hasn't
   * served to this instance yet.
   */ unservedResults: COUNT
}).strict();
const IntegrationLinkBackfillSchema = _zod.z.object({
    status: _zod.z.enum(_idis2golinks.IDIS2GO_BACKFILL_STATUSES),
    /** 0 to 100. Null while waiting. */ readPercent: _zod.z.number().int().min(0).max(100).nullable()
}).strict();
const IntegrationLinkSchema = _zod.z.object({
    linkId: _zod.z.uuid(),
    customerUuid: _zod.z.uuid(),
    enabled: _zod.z.boolean(),
    /** Null when the link never had a backfill and isn't waiting for one. */ backfill: IntegrationLinkBackfillSchema.nullable()
}).strict();
const MissingByReasonSchema = _zod.z.object({
    not_found: COUNT,
    no_media_row: COUNT,
    too_large: COUNT,
    bad_media_ref: COUNT,
    bad_media_path: COUNT
}).strict();
const ClinicIntegrationStatusResponseSchema = _zod.z.object({
    tenantId: _zod.z.uuid(),
    /** The database time the answer was read at. Ages are measured from it. */ checkedAt: _zod.z.iso.datetime(),
    /** Platform ingest's lag. The only platform value a clinic sees. */ ingest: IngestLagSchema,
    files: _zod.z.object({
        waiting: COUNT,
        copied: COUNT,
        missing: COUNT
    }).strict(),
    oldestWaitingFileAt: _zod.z.iso.datetime().nullable(),
    oldestWaitingFileAgeSeconds: SECONDS.nullable(),
    /** Files given up as missing in the last `windowDays` days, by reason. */ missingFiles: _zod.z.object({
        windowDays: _zod.z.number().int().positive(),
        byReason: MissingByReasonSchema
    }).strict(),
    results: ResultCountsSchema,
    /** Oldest first. */ emrInstances: _zod.z.array(ClinicIntegrationEmrInstanceSchema),
    /** Oldest first. Details are on the IDIS2GO tab. */ idis2goLinks: _zod.z.object({
        enabled: COUNT,
        items: _zod.z.array(IntegrationLinkSchema)
    }).strict()
}).strict();
